// LiveStreamBalanceWarning.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Animator from '../component/Animator.jsx';

import { ButtonId } from '../resource/trackingConstants.js';
import { formatTimeStringBySec } from '../resource/formatTimeString.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import getShouldShowLiveStreamBalanceWarning from '../resource/getShouldShowLiveStreamBalanceWarning.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import { color } from '../style/variables.js';
import CountDownImageSrc from '../../img/img-countdown.svg';
import ArrowRightIconSrc from '../../img/ic-arrow-right-white-12.svg';

const LiveStreamBalanceWarning = ({
  exclusivePrivateRunOutAt = null,
  remainingBalanceMultiple = 0,
  timestampOffsetSeconds = 0,
  addvalue = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();

  const renderBalanceWarning = () => {
    const now = getCurrentUnixTimestamp({
      offsetSeconds: timestampOffsetSeconds,
    });

    if (
      !getShouldShowLiveStreamBalanceWarning({
        exclusivePrivateRunOutAt,
        remainingBalanceMultiple,
        now,
      })
    )
      return null;

    const remainingSeconds = Math.max(exclusivePrivateRunOutAt - now, 0);

    return (
      <StyledLiveStreamBalanceWarning>
        <BalanceWarningContent>
          <CountDownImage src={CountDownImageSrc} alt="" />
          <TextColumn>
            <Title>
              {t('title_reminder_time_left', {
                ns: TranslationNamespace.LIVESTREAM,
              })}
            </Title>
            <Timer>
              {formatTimeStringBySec(remainingSeconds).replace(/:/g, ' : ')}
            </Timer>
          </TextColumn>
        </BalanceWarningContent>
        <AddvalueButton
          type="button"
          data-element_id={ButtonId.LiveStream.CountDownToDiamondShop}
          onClick={event => {
            event.preventDefault();
            addvalue();
          }}
        >
          {t('button_buy_now', { ns: TranslationNamespace.LIVESTREAM })}
          <img src={ArrowRightIconSrc} aria-hidden />
        </AddvalueButton>
      </StyledLiveStreamBalanceWarning>
    );
  };

  return <Animator render={renderBalanceWarning} fps={1} />;
};

LiveStreamBalanceWarning.propTypes = {
  exclusivePrivateRunOutAt: PropTypes.number,
  remainingBalanceMultiple: PropTypes.number,
  timestampOffsetSeconds: PropTypes.number,
  addvalue: PropTypes.func,
};

const StyledLiveStreamBalanceWarning = styled.div`
  position: absolute;
  top: 80px;
  right: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const BalanceWarningContent = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-bottom: -8px;
  padding: 12px 12px 12px 16px;
  border-top-left-radius: 100px;
  border-bottom-left-radius: 100px;
  background-color: ${color.neutral['0_alpha_80']};
`;

const CountDownImage = styled.img`
  flex-shrink: 0;
  width: auto;
  height: 56px;
`;

const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const Title = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.1px;
  white-space: nowrap;
  color: ${color.neutral[75]};
`;

const Timer = styled.div`
  width: 104px;
  font-size: 32px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  color: ${color.neutral[100]};
`;

const AddvalueButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 28px;
  padding: 0 8px;
  border: none;
  border-radius: 4px;
  background-image: linear-gradient(
    163deg,
    ${color.gamePurple} 0%,
    ${color.gamePurpleDeep} 100%
  );
  color: ${color.neutral[100]};
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.1px;
  cursor: pointer;
  > img {
    width: 16px;
    height: 16px;
  }
`;

export default LiveStreamBalanceWarning;
